import type {
  AttributeSchema,
  ResolvedAttributes,
} from "@/services/category.service";

/**
 * Which of the three backend modes a category's attribute row is in.
 *
 * A row can exist while still inheriting — `mode: 'inherit'` carries an empty
 * `attributes` object. Testing `!!meta.own` therefore misreports inheritance as
 * an override, which is the defect this module exists to prevent.
 */
export type InheritanceState = "own" | "inherited" | "empty";

export function attributeInheritanceState(
  meta: ResolvedAttributes | null,
): InheritanceState {
  const mode = meta?.own?.mode;
  if (mode === "override") return "own";
  if (mode === "empty") return "empty";
  return "inherited";
}

/** The attribute map the editor should start from for the current state. */
export function seedAttributes(
  meta: ResolvedAttributes | null,
): AttributeSchema {
  const state = attributeInheritanceState(meta);
  if (state === "empty") return {};
  const source =
    state === "own" ? meta?.own?.attributes : meta?.resolved?.attributes;
  return { ...(source ?? {}) };
}

/** Labels matching whichever attribute map `seedAttributes` chose. */
export function seedLabels(
  meta: ResolvedAttributes | null,
): Record<string, Record<string, string>> {
  return Object.fromEntries(
    Object.entries(seedAttributes(meta)).map(([key, definition]) => [
      key,
      { ...definition.label },
    ]),
  );
}

/**
 * Which of four states a title or description field is in.
 *
 * `catalogue-wide` and `none` are easy to conflate, and doing so is a bug: the
 * catalogue-wide scope genuinely cannot be overridden, whereas a top-level
 * category merely has no ancestor above it and must stay editable.
 */
export type TemplateState = "catalogue-wide" | "own" | "inherited" | "none";

export function templateFieldState(opts: {
  isCatalogueWide: boolean;
  hasOwn: boolean;
  inheritedFrom: string | null;
}): TemplateState {
  if (opts.isCatalogueWide) return "catalogue-wide";
  if (opts.hasOwn) return "own";
  return opts.inheritedFrom ? "inherited" : "none";
}
